import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AdSlot } from '../ads/AdSlot';
import { HomeScreenHint } from '../components/HomeScreenHint';
import { GameTile } from '../components/GameTile';
import { NavIcon, type IconName } from '../components/NavIcons';
import { LogoMark } from '../components/Wordmark';
import { CATALOG, CATEGORIES, findGame, soloPath, type CatalogEntry, type Category } from '../games/catalog';
import { HOME_DESCRIPTION, HOME_TITLE, setPageMeta } from '../lib/seo';
import popularData from '../games/popular.json';
import { LISTINGS, categoryListing, findListing, listingGames, modeListing, withCount, type Listing } from '../games/listings';
import { NotFound } from './NotFound';
import { useLibrary } from '../lib/library';
import { SuggestGame } from '../components/SuggestGame';
import { HOME_ABOUT } from '../games/siteAbout';

// Search pulls in every game guide (~400 kB), so it loads only when someone actually searches.
type SearchModule = typeof import('../games/search');
let searchMod: SearchModule | null = null;
function useSearchModule(active: boolean): SearchModule | null {
  const [, setLoaded] = useState(searchMod !== null);
  useEffect(() => {
    if (!active || searchMod) return;
    let live = true;
    import('../games/search').then(
      (m) => {
        searchMod = m;
        if (live) setLoaded(true);
      },
      () => {},
    );
    return () => {
      live = false;
    };
  }, [active]);
  return searchMod;
}

type ModeFilter = 'solo' | 'multi' | 'keyboard' | 'touch';

interface Heading {
  emoji: string;
  text: string;
}

const MODE_FILTERS: Record<ModeFilter, { heading: Heading; test: (g: CatalogEntry) => boolean }> = {
  solo: { heading: { emoji: '🤖', text: 'Solo Mode: You vs the Bot!' }, test: (g) => g.modes.includes('solo') },
  multi: { heading: { emoji: '👥', text: 'Two Players, One Device!' }, test: (g) => g.modes.includes('keyboard') || g.modes.includes('touch') },
  keyboard: { heading: { emoji: '⌨️', text: 'One Keyboard, Two Rivals!' }, test: (g) => g.modes.includes('keyboard') },
  touch: { heading: { emoji: '👆', text: 'Tap, Click & Take Turns!' }, test: (g) => g.modes.includes('touch') },
};

const CATEGORY_HEADINGS: Record<Category, Heading> = {
  shooter: { emoji: '💥', text: 'Aim, Ricochet, Boom!' },
  sports: { emoji: '⚽', text: 'Game On! Sports Showdowns' },
  racing: { emoji: '🏎️', text: 'Floor It! Racing & Stunts' },
  arcade: { emoji: '🕹️', text: 'Fast Hands Win: Arcade Battles' },
  clicker: { emoji: '🍪', text: 'Click, Grow, Get Rich!' },
  sandbox: { emoji: '⛏️', text: 'Build, Explore, Survive!' },
  party: { emoji: '🎉', text: 'Quick, Loud & Silly Party Games' },
  board: { emoji: '🎲', text: 'Classic Board Games: Your Move!' },
  strategy: { emoji: '🧠', text: 'Outsmart Your Rival!' },
  puzzle: { emoji: '🧩', text: 'Think Fast, Match Faster!' },
};

/** The homepage's one headline. */
const HOME_HEADLINE = 'Best Free Unblocked Games Online!';

/** Most-played games from real visits (scripts/update-popular.mjs), topped up with picks while data is thin. */
/** Backup for the Popular row (real plays as of the last build); the live list comes from /api/popular. */
const POPULAR: string[] = popularData.popular;

/** The most-played games from real visits (worker/index.ts), falling back to the build-time list. */
function usePopular(): string[] {
  const [ids, setIds] = useState(POPULAR);
  useEffect(() => {
    let alive = true;
    fetch('/api/popular')
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { popular?: unknown } | null) => {
        const list = Array.isArray(d?.popular) ? d.popular.filter((x): x is string => typeof x === 'string') : [];
        if (alive && list.length) setIds(list);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return ids;
}

/** Games that top up the Popular row after the real most-played ones, so it always ends on a full row. */
const POPULAR_FILL = ['storm-royale', 'basketball', 'block-world', 'turbo-ball', 'cookie-factory', 'brainrot-heist', 'kart-racer', 'rail-runner', 'grow-garden', 'snake-arena', 'brawl-arena', 'soccer', 'block-drop', 'chess', 'slingshot-smash', 'fruit-slicer'];
const withFill = (ids: string[]) => [...new Set([...ids, ...POPULAR_FILL, ...CATALOG.map((g) => g.id)])];

/** Short, pick-up-and-play games for the homepage's Quick Break row. */
const QUICK_BREAK = ['flap-dash', 'desert-dino', 'sunset-roll', 'cube-dash', 'tower-stack', 'fruit-slicer', 'road-hopper', 'penalty-kicks', 'spring-jumper', 'merge-2048', 'classic-snake', 'aim-trainer', 'block-drop', 'bubble-pop', 'quick-draw', 'brick-breaker', 'slingshot-smash', 'moto-stunts'];

const pick = (ids: string[]) => ids.map((id) => findGame(id)).filter((g): g is CatalogEntry => !!g);

const indexOf = (g: CatalogEntry) => CATALOG.indexOf(g);

/** Big double-size tiles on the main grid. */
const FEATURED = new Set([
  'turbo-ball', 'storm-royale', 'block-world', 'brainrot-heist', 'cookie-factory', 'grow-garden', 'basketball', 'kart-racer',
  'soccer', 'rail-runner', 'chess', 'golf', 'keep-rush', 'super-hop', 'bowling',
]);

/**
 * Scatters the big tiles evenly through the grid instead of bunching them at
 * the top; the dense mosaic tucks small tiles around each one.
 */
function spreadFeatured(games: CatalogEntry[], featured: ReadonlySet<string>): CatalogEntry[] {
  const big = games.filter((g) => featured.has(g.id));
  const small = games.filter((g) => !featured.has(g.id));
  if (!big.length) return small;
  const gap = Math.max(3, Math.floor(small.length / big.length));
  const out: CatalogEntry[] = [];
  big.forEach((b, i) => {
    out.push(b, ...small.slice(i * gap, (i + 1) * gap));
  });
  out.push(...small.slice(big.length * gap));
  return out;
}

function Section({
  heading,
  games,
  tone,
  id,
  solo = false,
  twoPlayer = false,
  feature = false,
  fillRows,
  note,
}: {
  /** Leave out for a grid with no title bar (the homepage's one big grid). */
  heading?: Heading;
  games: CatalogEntry[];
  tone: number;
  id: string;
  solo?: boolean;
  /** Link tiles straight to the two-player mode (games default to vs Bot). */
  twoPlayer?: boolean;
  /** Big 2x2 tiles: true for the usual picks, or a set of game ids. */
  feature?: boolean | ReadonlySet<string>;
  /** Show only whole rows (at least two) of `games`, sized to fit the first `keep` of them. */
  fillRows?: { keep: number };
  /** Small line under the heading. */
  note?: string;
}) {
  const listRef = useRef<HTMLUListElement>(null);
  const [cols, setCols] = useState(0);
  useLayoutEffect(() => {
    const el = listRef.current;
    if (!fillRows || !el) return;
    // The grid's column count changes with screen width, so measure it rather than guess.
    const measure = () => setCols(getComputedStyle(el).gridTemplateColumns.split(' ').filter(Boolean).length);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [!!fillRows]);
  const shown = fillRows ? games.slice(0, cols ? cols * Math.max(2, Math.ceil(fillRows.keep / cols)) : 14) : games;
  const featured: ReadonlySet<string> = feature === true ? FEATURED : feature || new Set();
  const ordered = featured.size ? spreadFeatured(shown, featured) : shown;
  return (
    <section aria-labelledby={heading ? id : undefined} aria-label={heading ? undefined : 'All games'}>
      {heading && (
        <div className="band band--heading">
          <h2 id={id}>
            <span aria-hidden="true">{heading.emoji} </span>
            {heading.text}
            <span aria-hidden="true"> {heading.emoji}</span>
          </h2>
          {note && <p className="band__note">{note}</p>}
        </div>
      )}
      <div className={`band band--grid band--tone-${tone % 2}`}>
        {games.length ? (
          <ul className="tiles" ref={listRef}>
            {ordered.map((g) => {
              const big = featured.has(g.id);
              return (
                <li key={g.id} className={big ? 'tiles__big' : undefined}>
                  <GameTile game={g} index={indexOf(g)} to={solo ? soloPath(g) : twoPlayer ? `${g.path}?vs=2p` : undefined} featured={big} />
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="band__empty">
            No games match. <Link to="/">See all games</Link>
          </p>
        )}
      </div>
    </section>
  );
}

/**
 * One row of games that scrolls sideways, with arrow buttons on computers (like the big game sites).
 * `feature` makes the first game a big tile with the others in two rows beside it.
 */
function Row({ id, icon, title, games, more, feature = false }: { id: string; icon: IconName; title: string; games: CatalogEntry[]; more?: string; feature?: boolean }) {
  const track = useRef<HTMLUListElement>(null);
  const [ends, setEnds] = useState({ start: true, end: false });
  const update = () => {
    const el = track.current;
    if (el) setEnds({ start: el.scrollLeft < 4, end: el.scrollLeft + el.clientWidth > el.scrollWidth - 4 });
  };
  useLayoutEffect(() => {
    const el = track.current;
    // Every row starts at its first game.
    if (el) el.scrollLeft = 0;
    update();
    if (!el) return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [games.map((g) => g.id).join()]);
  const scroll = (dir: number) => track.current?.scrollBy({ left: dir * track.current.clientWidth * 0.85, behavior: 'smooth' });
  if (!games.length) return null;
  return (
    <section className={`row${feature ? ' row--feature' : ''}`} aria-labelledby={id}>
      <div className="row__head">
        <h2 id={id} className="row__title">
          <span className="row__icon">
            <NavIcon name={icon} size={24} />
          </span>
          {title}
        </h2>
        {more && (
          <Link to={more} className="row__all">
            View all
          </Link>
        )}
      </div>
      <div className="row__body">
        {!ends.start && (
          <button type="button" className="row__arrow row__arrow--prev" aria-label={`Scroll ${title} back`} onClick={() => scroll(-1)}>
            ‹
          </button>
        )}
        <ul className="row__track" ref={track} onScroll={update}>
          {games.map((g, i) => (
            <li key={g.id} className={feature && i === 0 ? 'row__big' : undefined}>
              <GameTile game={g} index={indexOf(g)} featured={feature && i === 0} autoPreview={feature && i === 0} />
            </li>
          ))}
        </ul>
        {!ends.end && (
          <button type="button" className="row__arrow row__arrow--next" aria-label={`Scroll ${title} forward`} onClick={() => scroll(1)}>
            ›
          </button>
        )}
      </div>
    </section>
  );
}

export function Home() {
  const [params] = useSearchParams();
  const { slug } = useParams();
  const popular = usePopular();
  const library = useLibrary();
  const rank = new Map(withFill(popular).map((id, i) => [id, i]));
  const listing = slug ? findListing(slug) : undefined;
  const c = params.get('c');
  const q = params.get('q')?.trim().toLowerCase() ?? '';
  const search = useSearchModule(q !== '');

  useEffect(() => {
    if (listing) setPageMeta(listing.title, withCount(listing.description, listing), `/games/${listing.slug}`);
    else setPageMeta(HOME_TITLE, HOME_DESCRIPTION, '/');
  }, [listing]);

  if (slug && !listing) return <NotFound />;
  // Old filter links (/?c=racing) moved to their own pages.
  const moved = !slug && c ? (CATEGORIES.some((x) => x.id === c) ? categoryListing(c as Category) : c === 'solo' || c === 'multi' ? modeListing(c) : undefined) : undefined;
  if (moved) return <Navigate replace to={`/games/${moved.slug}`} />;

  let body;
  if (listing) {
    const f = listing.filter;
    const heading = 'category' in f ? CATEGORY_HEADINGS[f.category] : MODE_FILTERS[f.mode].heading;
    body = (
      <>
        <Section id="cat-h" heading={heading} games={listingGames(listing)} tone={0} solo={'mode' in f && f.mode === 'solo'} twoPlayer={'mode' in f && f.mode === 'multi'} />
        <ListingAbout listing={listing} />
      </>
    );
  } else if (q && !search) {
    body = null;
  } else if (q && search) {
    const games = search.searchGames(q);
    body = games.length ? (
      <Section id="results-h" heading={{ emoji: '🔍', text: `Results for “${params.get('q')}”` }} games={games} tone={0} />
    ) : (
      <>
        <Section
          id="results-h"
          heading={{ emoji: '🔍', text: `No games called “${params.get('q')}” yet` }}
          note="Try one of these popular games, or ask us to make it with the Suggest a Game form below!"
          games={pick(withFill(popular)).slice(0, 14)}
          tone={0}
        />
        <SuggestGame />
      </>
    );
  } else if (c && c in MODE_FILTERS) {
    const f = MODE_FILTERS[c as ModeFilter];
    body = <Section id="mode-h" heading={f.heading} games={CATALOG.filter(f.test)} tone={0} solo={c === 'solo'} twoPlayer={c !== 'solo'} />;
  } else {
    body = (
      <>
        {/* Rows of games, like the big game sites: new, most played, then one row per kind of game. */}
        <div className="band rows">
          {library.recent.length > 0 && <Row id="recent-h" icon="plays" title="Jump Back In" games={pick(library.recent)} />}
          {library.favorites.length > 0 && <Row id="favorites-h" icon="heart" title="Your Favorites" games={pick(library.favorites)} />}
          <Row id="popular-h" icon="popular" title="Popular Games" games={pick(withFill(popular)).slice(0, 25)} feature />
          <Row id="quick-h" icon="clock" title="Quick Break" games={pick(QUICK_BREAK)} />
          <Row id="friends-h" icon="multi" title="Play With Friends" games={CATALOG.filter((g) => g.modes.includes('keyboard') || g.modes.includes('touch')).sort((a, b) => (rank.get(a.id) ?? 999) - (rank.get(b.id) ?? 999))} more="/games/2-player" />
          {CATEGORIES.map((cat) => {
            const l = categoryListing(cat.id);
            const games = CATALOG.filter((g) => g.category === cat.id).sort((a, b) => (rank.get(a.id) ?? 999) - (rank.get(b.id) ?? 999));
            return <Row key={cat.id} id={`row-${cat.id}`} icon={cat.id} title={l ? l.h1.replace(/^Unblocked /, '') : cat.label} games={games} more={l ? `/games/${l.slug}` : undefined} />;
          })}
        </div>
        <section className="band band--about" aria-labelledby="home-about-h">
          <div className="about-text">
            <h2 id="home-about-h">{HOME_ABOUT.heading}</h2>
            {HOME_ABOUT.paragraphs.map((p) => (
              <p key={p}>{p}</p>
            ))}
            <p>
              <Link to="/about">More about us</Link> · <Link to="/parents">For parents and teachers</Link>
            </p>
          </div>
        </section>
        <SuggestGame />
      </>
    );
  }

  return (
    <main id="main" className="home">
      {listing ? (
        <section className="band band--hero band--hero-center">
          <div className="hero__text">
            <h1 className="hero__title">{listing.h1}</h1>
            <p className="hero__sub">{withCount(listing.intro, listing)}</p>
          </div>
        </section>
      ) : (
        // One slim bar, like the big game sites: logo, name and search, so the games start right below it.
        <header className="home-head">
          <h1 className="visually-hidden">{HOME_HEADLINE}</h1>
          <Link to="/" className="home-head__brand" aria-label="SuperFunUBG home">
            <LogoMark size={36} />
            <span>
              SuperFun<span className="brand__ubg">UBG</span>
            </span>
          </Link>
          <HomeSearch initial={params.get('q') ?? ''} />
        </header>
      )}

      <HomeScreenHint />
      {body}

      <AdSlot placement="home" className="ad--band" />
    </main>
  );
}

/** The text under a category page's games, plus links to the other category pages. */
function ListingAbout({ listing }: { listing: Listing }) {
  return (
    <section className="band band--about" aria-labelledby="about-h">
      <div className="about-text">
        <h2 id="about-h">About these games</h2>
        {listing.about.map((p) => (
          <p key={p}>{withCount(p, listing)}</p>
        ))}
        <h3>More game categories</h3>
        <ul className="about-links">
          {LISTINGS.filter((l) => l.slug !== listing.slug).map((l) => (
            <li key={l.slug}>
              <Link to={`/games/${l.slug}`}>{l.h1.replace(/^Unblocked /, '')}</Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/** Big search bar on the homepage: the toolbar's search icon is easy to miss. */
function HomeSearch({ initial }: { initial: string }) {
  const navigate = useNavigate();
  const [value, setValue] = useState(initial);
  useEffect(() => setValue(initial), [initial]);
  const go = (v: string) => {
    setValue(v);
    navigate(v.trim() ? `/?q=${encodeURIComponent(v.trim())}` : '/', { replace: true });
  };
  return (
    <form className="home-search" role="search" onSubmit={(e) => e.preventDefault()}>
      <span className="home-search__icon" aria-hidden="true">
        🔍
      </span>
      <input
        type="search"
        aria-label="Search games"
        placeholder="Search from 100+ games"
        value={value}
        autoComplete="off"
        enterKeyHint="search"
        onChange={(e) => go(e.target.value)}
      />
    </form>
  );
}
